import { createFileRoute, notFound, redirect, useRouter } from "@tanstack/react-router";
import { MonthPage } from "@/components/pages/MonthPage";
import { DatePage } from "@/components/pages/DatePage";
import { SiteShell } from "@/components/layout/SiteShell";
import {
  parseYearSlug,
  parseBareMonthSlug,
  parseBareDateSlug,
  parseBareMonthSlugEn,
  parseBareDateSlugEn,
  daysInMonth,
  MONTH_NAMES_ID,
  MONTH_SLUGS_ID,
} from "@/lib/calendar/dates";
import { withSlash } from "@/lib/url";

type Resolved =
  | { kind: "month"; y: number; m: number }
  | { kind: "date"; y: number; m: number; d: number };

function resolve(year: string, slug: string): Resolved | null {
  const y = parseYearSlug(year);
  if (y === null) return null;
  const mo = parseBareMonthSlug(slug);
  if (mo) return { kind: "month", y, m: mo };
  const md = parseBareDateSlug(slug);
  if (md) {
    if (md.d > daysInMonth(y, md.m)) return null;
    return { kind: "date", y, m: md.m, d: md.d };
  }
  return null;
}

export const Route = createFileRoute("/kalender/$year/$slug")({
  loader: ({ params }) => {
    // Legacy/Googlebot-guessed EN month slugs under /kalender/{year}/...
    // → 308-redirect to canonical ID slug. Preserves SEO equity.
    const y0 = parseYearSlug(params.year);
    if (y0 !== null) {
      const enMo = parseBareMonthSlugEn(params.slug);
      if (enMo) {
        const idSlug = MONTH_SLUGS_ID[enMo - 1];
        if (idSlug !== params.slug) {
          throw redirect({ to: `/kalender/${y0}/${idSlug}/`, code: 308 } as any);
        }
      }
      const enDt = parseBareDateSlugEn(params.slug);
      if (enDt) {
        const idSlug = `${enDt.d}-${MONTH_SLUGS_ID[enDt.m - 1]}`;
        if (idSlug !== params.slug) {
          throw redirect({ to: `/kalender/${y0}/${idSlug}/`, code: 308 } as any);
        }
      }
    }
    const r = resolve(params.year, params.slug);
    if (!r) throw notFound();
    return r;
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return {};
    let title = "Kalender Indonesia";
    let desc = "Kalender Indonesia lengkap.";
    if (loaderData.kind === "month") {
      const mn = MONTH_NAMES_ID[loaderData.m - 1];
      title = `Kalender ${mn} ${loaderData.y} - Libur, Hijriah & Jawa`;
      desc = `Kalender ${mn} ${loaderData.y} lengkap: tanggal merah, cuti bersama, Hijriah, weton Jawa, Bali.`;
    } else {
      const mn = MONTH_NAMES_ID[loaderData.m - 1];
      title = `${loaderData.d} ${mn} ${loaderData.y} - Hari Apa, Weton & Hijriah`;
      desc = `Detail tanggal ${loaderData.d} ${mn} ${loaderData.y}: hari, weton Jawa, Hijriah, Bali, dan status libur.`;
    }
    const canonical = `https://hari.co.id${withSlash(`/kalender/${params.year}/${params.slug}`)}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: canonical },
        { property: "og:locale", content: "id_ID" },
        { property: "og:locale:alternate", content: "en_US" },
      ],
      links: [{ rel: "canonical", href: canonical }],
    };
  },
  component: SlugPage,
  notFoundComponent: () => (
    <SiteShell>
      <div className="py-16 text-center">
        <h1 className="text-2xl font-bold">Halaman tidak ditemukan</h1>
        <p className="mt-2 text-muted-foreground">URL tidak cocok format bulan atau tanggal yang valid.</p>
      </div>
    </SiteShell>
  ),
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <SiteShell>
        <div className="py-16 text-center">
          <h1 className="text-2xl font-bold">Terjadi kesalahan</h1>
          <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
          <button onClick={() => { router.invalidate(); reset(); }} className="mt-4 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground">
            Coba lagi
          </button>
        </div>
      </SiteShell>
    );
  },
});

function SlugPage() {
  const data = Route.useLoaderData();
  if (data.kind === "month") return <MonthPage y={data.y} m={data.m} />;
  return <DatePage y={data.y} m={data.m} d={data.d} />;
}